import type { Card, HandEvaluation } from '@/types';
import { HandRank } from '@/types';

export function evaluateHand(holeCards: [Card, Card], communityCards: Card[]): HandEvaluation {
  const allCards = [...holeCards, ...communityCards];
  
  // 检查各种牌型（从高到低）
  const royalFlush = checkRoyalFlush(allCards);
  if (royalFlush) return { ...royalFlush, rank: HandRank.ROYAL_FLUSH, name: '皇家同花顺' };
  
  const straightFlush = checkStraightFlush(allCards);
  if (straightFlush) return { ...straightFlush, rank: HandRank.STRAIGHT_FLUSH, name: '同花顺' };
  
  const fourOfAKind = checkFourOfAKind(allCards);
  if (fourOfAKind) return { ...fourOfAKind, rank: HandRank.FOUR_OF_A_KIND, name: '四条' };
  
  const fullHouse = checkFullHouse(allCards);
  if (fullHouse) return { ...fullHouse, rank: HandRank.FULL_HOUSE, name: '葫芦' };
  
  const flush = checkFlush(allCards);
  if (flush) return { ...flush, rank: HandRank.FLUSH, name: '同花' };
  
  const straight = checkStraight(allCards);
  if (straight) return { ...straight, rank: HandRank.STRAIGHT, name: '顺子' };
  
  const threeOfAKind = checkThreeOfAKind(allCards);
  if (threeOfAKind) return { ...threeOfAKind, rank: HandRank.THREE_OF_A_KIND, name: '三条' };
  
  const twoPair = checkTwoPair(allCards);
  if (twoPair) return { ...twoPair, rank: HandRank.TWO_PAIR, name: '两对' };
  
  const onePair = checkOnePair(allCards);
  if (onePair) return { ...onePair, rank: HandRank.ONE_PAIR, name: '一对' };
  
  const highCard = checkHighCard(allCards);
  return { ...highCard, rank: HandRank.HIGH_CARD, name: '高牌' };
}

// 辅助函数：按牌值分组
function groupByRank(cards: Card[]): Map<number, Card[]> {
  const groups = new Map<number, Card[]>();
  for (const card of cards) {
    if (!groups.has(card.value)) {
      groups.set(card.value, []);
    }
    groups.get(card.value)!.push(card);
  }
  return groups;
}

// 辅助函数：按花色分组
function groupBySuit(cards: Card[]): Map<string, Card[]> {
  const groups = new Map<string, Card[]>();
  for (const card of cards) {
    if (!groups.has(card.suit)) {
      groups.set(card.suit, []);
    }
    groups.get(card.suit)!.push(card);
  }
  return groups;
}



// 检查皇家同花顺
function checkRoyalFlush(cards: Card[]): Omit<HandEvaluation, 'rank' | 'name'> | null {
  const suitGroups = groupBySuit(cards);
  
  for (const [, suitedCards] of suitGroups) {
    if (suitedCards.length >= 5) {
      const sorted = suitedCards.sort((a, b) => b.value - a.value);
      const royalValues = [14, 13, 12, 11, 10];
      const hasRoyal = royalValues.every(val => sorted.some(c => c.value === val));
      
      if (hasRoyal) {
        const royalCards = royalValues.map(val => 
          sorted.find(c => c.value === val)!
        );
        return {
          cards: royalCards,
          kickers: [],
          value: 10 ** 10 + royalCards[0].value,
        };
      }
    }
  }
  return null;
}

// 检查同花顺
function checkStraightFlush(cards: Card[]): Omit<HandEvaluation, 'rank' | 'name'> | null {
  const suitGroups = groupBySuit(cards);
  
  for (const [, suitedCards] of suitGroups) {
    if (suitedCards.length >= 5) {
      const straight = findStraight(suitedCards);
      if (straight) {
        return {
          cards: straight,
          kickers: [],
          value: 10 ** 9 + straight[0].value,
        };
      }
    }
  }
  return null;
}

// 检查四条
function checkFourOfAKind(cards: Card[]): Omit<HandEvaluation, 'rank' | 'name'> | null {
  const rankGroups = groupByRank(cards);
  
  for (const [value, group] of rankGroups) {
    if (group.length === 4) {
      const kickers = cards
        .filter(c => c.value !== value)
        .sort((a, b) => b.value - a.value)
        .slice(0, 1);
      return {
        cards: group,
        kickers,
        value: 10 ** 8 + value * 100 + (kickers[0]?.value || 0),
      };
    }
  }
  return null;
}

// 检查葫芦
function checkFullHouse(cards: Card[]): Omit<HandEvaluation, 'rank' | 'name'> | null {
  const rankGroups = groupByRank(cards);
  let threeOfAKind: Card[] | null = null;
  let pair: Card[] | null = null;
  
  // 找最大的三条
  for (const [, group] of [...rankGroups.entries()].sort((a, b) => b[0] - a[0])) {
    if (group.length >= 3 && !threeOfAKind) {
      threeOfAKind = group.slice(0, 3);
    } else if (group.length >= 2 && !pair) {
      pair = group.slice(0, 2);
    }
    
    if (threeOfAKind && pair) break;
  }
  
  if (threeOfAKind && pair) {
    return {
      cards: [...threeOfAKind, ...pair],
      kickers: [],
      value: 10 ** 7 + threeOfAKind[0].value * 100 + pair[0].value,
    };
  }
  return null;
}

// 检查同花
function checkFlush(cards: Card[]): Omit<HandEvaluation, 'rank' | 'name'> | null {
  const suitGroups = groupBySuit(cards);
  
  for (const [, suitedCards] of suitGroups) {
    if (suitedCards.length >= 5) {
      const sorted = suitedCards.sort((a, b) => b.value - a.value).slice(0, 5);
      return {
        cards: sorted,
        kickers: [],
        value: 10 ** 6 + sorted.reduce((acc, c, i) => acc + c.value * (10 ** (4 - i)), 0),
      };
    }
  }
  return null;
}

// 检查顺子
function checkStraight(cards: Card[]): Omit<HandEvaluation, 'rank' | 'name'> | null {
  const straight = findStraight(cards);
  if (straight) {
    return {
      cards: straight,
      kickers: [],
      value: 10 ** 5 + straight[0].value,
    };
  }
  return null;
}

// 查找顺子
function findStraight(cards: Card[]): Card[] | null {
  // 去重并按值排序
  const uniqueValues = new Map<number, Card>();
  for (const card of cards) {
    if (!uniqueValues.has(card.value) || card.suit === 'spades') {
      uniqueValues.set(card.value, card);
    }
  }
  
  const sorted = [...uniqueValues.values()].sort((a, b) => b.value - a.value);
  
  // 检查普通顺子
  for (let i = 0; i <= sorted.length - 5; i++) {
    const seq = sorted.slice(i, i + 5);
    if (isConsecutive(seq.map(c => c.value))) {
      return seq;
    }
  }
  
  // 检查 A-5 顺子（A作为1）
  const hasAce = sorted.some(c => c.value === 14);
  if (hasAce) {
    const lowStraight = [5, 4, 3, 2].map(val => 
      sorted.find(c => c.value === val)
    ).filter(Boolean) as Card[];
    
    if (lowStraight.length === 4) {
      const ace = sorted.find(c => c.value === 14)!;
      return [...lowStraight, ace];
    }
  }
  
  return null;
}

function isConsecutive(values: number[]): boolean {
  for (let i = 1; i < values.length; i++) {
    if (values[i-1] - values[i] !== 1) return false;
  }
  return true;
}

// 检查三条
function checkThreeOfAKind(cards: Card[]): Omit<HandEvaluation, 'rank' | 'name'> | null {
  const rankGroups = groupByRank(cards);
  
  for (const [cardValue, group] of [...rankGroups.entries()].sort((a, b) => b[0] - a[0])) {
    if (group.length === 3) {
      const kickers = cards
        .filter(c => c.value !== cardValue)
        .sort((a, b) => b.value - a.value)
        .slice(0, 2);
      return {
        cards: group,
        kickers,
        value: 10 ** 4 + cardValue * 1000 + kickers.reduce((acc, c, i) => acc + c.value * (10 ** (1 - i)), 0),
      };
    }
  }
  return null;
}

// 检查两对
function checkTwoPair(cards: Card[]): Omit<HandEvaluation, 'rank' | 'name'> | null {
  const rankGroups = groupByRank(cards);
  const pairs: Card[][] = [];
  
  for (const [, group] of [...rankGroups.entries()].sort((a, b) => b[0] - a[0])) {
    if (group.length >= 2) {
      pairs.push(group.slice(0, 2));
    }
    if (pairs.length === 2) break;
  }
  
  if (pairs.length === 2) {
    const pairValues = pairs.map(p => p[0].value);
    const kickers = cards
      .filter(c => !pairValues.includes(c.value))
      .sort((a, b) => b.value - a.value)
      .slice(0, 1);
    
    const allCards = [...pairs[0], ...pairs[1]];
    return {
      cards: allCards,
      kickers,
      value: 10 ** 3 + pairs[0][0].value * 100 + pairs[1][0].value * 10 + (kickers[0]?.value || 0),
    };
  }
  return null;
}

// 检查一对
function checkOnePair(cards: Card[]): Omit<HandEvaluation, 'rank' | 'name'> | null {
  const rankGroups = groupByRank(cards);
  
  for (const [value, group] of [...rankGroups.entries()].sort((a, b) => b[0] - a[0])) {
    if (group.length === 2) {
      const kickers = cards
        .filter(c => c.value !== value)
        .sort((a, b) => b.value - a.value)
        .slice(0, 3);
      return {
        cards: group,
        kickers,
        value: 10 ** 2 + value * 1000 + kickers.reduce((acc, c, i) => acc + c.value * (10 ** (2 - i)), 0),
      };
    }
  }
  return null;
}

// 检查高牌
function checkHighCard(cards: Card[]): Omit<HandEvaluation, 'rank' | 'name'> {
  const sorted = cards.sort((a, b) => b.value - a.value).slice(0, 5);
  return {
    cards: [sorted[0]],
    kickers: sorted.slice(1),
    value: sorted.reduce((acc, c, i) => acc + c.value * (10 ** (4 - i)), 0),
  };
}

// 比较两手牌的大小
export function compareHands(hand1: HandEvaluation, hand2: HandEvaluation): number {
  if (hand1.rank !== hand2.rank) {
    return hand1.rank - hand2.rank;
  }
  return hand1.value - hand2.value;
}
